import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Mail,
  Phone as PhoneIcon,
  MessageSquare,
  ShieldCheck,
  CheckCircle2,
  AlertCircle,
  Edit3,
  Trash2,
  X,
  Plus,
  Volume2,
  RefreshCw,
  Copy,
  Download,
  Star,
} from "lucide-react";
import PhoneInput, { isValidPhoneNumber } from "react-phone-number-input";
import "react-phone-number-input/style.css";
import JeaCard from "@/components/jea/JeaCard";
import JeaButton from "@/components/jea/JeaButton";
import JeaInput from "@/components/jea/JeaInput";
import JeaToggle from "@/components/jea/JeaToggle";
import JeaBadge from "@/components/jea/JeaBadge";
import { apiClient, contactApi, formatApiError } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import { cn } from "@/lib/utils";

const CHANNEL_LABEL = {
  email: "Email",
  sms: "Text Message (SMS)",
  totp: "Authenticator App",
  voice: "Voice Call",
  backup: "Backup Codes",
};
const CHANNEL_ICON = {
  email: Mail,
  sms: MessageSquare,
  totp: ShieldCheck,
  voice: PhoneIcon,
};

const Section = ({ label, children, testid }) => (
  <div className="mb-8" data-testid={testid}>
    <div className="jea-caption text-accent-pink mb-2 pb-2 border-b border-border-default">
      {label}
    </div>
    {children}
  </div>
);

/* ========================= ContactMethodsPanel ========================= */
const ContactMethodsPanel = ({ onChange }) => {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(null); // "add-email" | "add-phone" | ...
  const [emailStage, setEmailStage] = useState("idle"); // idle | editing | verifying
  const [phoneStage, setPhoneStage] = useState("idle");
  const [emailNew, setEmailNew] = useState("");
  const [phoneNew, setPhoneNew] = useState("");
  const [emailCode, setEmailCode] = useState("");
  const [phoneCode, setPhoneCode] = useState("");

  const load = async () => {
    try {
      const d = await contactApi.get();
      setData(d);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  useEffect(() => { load(); }, []);

  const refresh = async () => { await load(); onChange?.(); };

  if (!data) {
    return <div className="text-text-muted text-[13px]">Loading contact methods…</div>;
  }

  const startEmail = async () => {
    if (!emailNew) { toast.error("Email required."); return; }
    setBusy("start-email");
    try {
      await contactApi.updateEmail(emailNew);
      toast.success(`Verification code sent to ${emailNew}.`);
      setEmailStage("verifying");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(null); }
  };
  const confirmEmail = async () => {
    setBusy("confirm-email");
    try {
      const r = await contactApi.confirmEmail(emailCode);
      toast.success(`Email verified: ${r.email}`);
      setEmailStage("idle"); setEmailNew(""); setEmailCode("");
      await refresh();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(null); }
  };
  const removeEmail = async () => {
    setBusy("remove-email");
    try {
      await contactApi.removeEmail();
      toast.success("Email removed.");
      await refresh();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(null); }
  };

  const startPhone = async () => {
    if (!phoneNew || !isValidPhoneNumber(phoneNew)) {
      toast.error("Enter a valid phone number including country code.");
      return;
    }
    setBusy("start-phone");
    try {
      await contactApi.updatePhone(phoneNew);
      toast.success(`Verification code sent to ${phoneNew}.`);
      setPhoneStage("verifying");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(null); }
  };
  const confirmPhone = async () => {
    setBusy("confirm-phone");
    try {
      const r = await contactApi.confirmPhone(phoneCode);
      toast.success(`Phone verified: ${r.phone_number}`);
      setPhoneStage("idle"); setPhoneNew(""); setPhoneCode("");
      await refresh();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(null); }
  };
  const removePhone = async () => {
    setBusy("remove-phone");
    try {
      await contactApi.removePhone();
      toast.success("Phone removed.");
      await refresh();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(null); }
  };

  const digitInput = (val, onChange, prefix) => (
    <div className="flex gap-2">
      {Array.from({ length: 6 }).map((_, i) => {
        const digits = val.padEnd(6, " ").split("").map((c) => c === " " ? "" : c);
        return (
          <input
            key={i}
            inputMode="numeric"
            maxLength={1}
            value={digits[i]}
            onChange={(e) => {
              const d = e.target.value.replace(/\D/g, "").slice(-1);
              const next = [...digits];
              next[i] = d;
              onChange(next.join("").replace(/\s/g, ""));
              if (d && i < 5) {
                const el = document.querySelector(`[data-testid="${prefix}-${i + 1}"]`);
                if (el) el.focus();
              }
            }}
            onKeyDown={(e) => {
              if (e.key === "Backspace" && !digits[i] && i > 0) {
                const el = document.querySelector(`[data-testid="${prefix}-${i - 1}"]`);
                if (el) el.focus();
              }
            }}
            data-testid={`${prefix}-${i}`}
            className={cn(
              "w-10 h-12 rounded-md text-center text-lg font-bold text-white",
              "bg-bg-tertiary border border-border-default outline-none",
              "focus:border-success focus:shadow-[0_0_0_4px_rgba(46,204,113,0.30)]",
              "transition-all duration-150",
            )}
          />
        );
      })}
    </div>
  );

  return (
    <div className="space-y-4" data-testid="contact-methods-panel">
      {/* Email row */}
      <div
        className="rounded-xl border border-border-default bg-bg-primary p-4 space-y-3"
        data-testid="contact-email-row"
      >
        <div className="flex items-start gap-3">
          <Mail size={20} className="text-success mt-0.5" />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-white text-[14px] font-semibold">Email</span>
              {data.email_verified ? (
                <JeaBadge variant="success" data-testid="contact-email-verified-badge">
                  <CheckCircle2 size={12} /> Verified
                </JeaBadge>
              ) : data.email ? (
                <JeaBadge variant="warning" data-testid="contact-email-unverified-badge">
                  <AlertCircle size={12} /> Unverified
                </JeaBadge>
              ) : (
                <JeaBadge variant="neutral" data-testid="contact-email-none-badge">Not set</JeaBadge>
              )}
            </div>
            <div className="text-text-secondary text-[13px] mt-0.5" data-testid="contact-email-value">
              {data.email || "—"}
            </div>
          </div>
          {emailStage === "idle" && (
            <div className="flex gap-2">
              <JeaButton
                size="sm"
                variant="outline-neutral"
                onClick={() => { setEmailStage("editing"); setEmailNew(""); }}
                data-testid="contact-email-change-btn"
              >
                <Edit3 size={14} /> {data.email ? "Change" : "Add"}
              </JeaButton>
              {data.email && (
                <JeaButton
                  size="sm"
                  variant="outline-neutral"
                  onClick={removeEmail}
                  loading={busy === "remove-email"}
                  data-testid="contact-email-remove-btn"
                >
                  <Trash2 size={14} />
                </JeaButton>
              )}
            </div>
          )}
        </div>
        {emailStage === "editing" && (
          <div className="space-y-2 pl-7" data-testid="contact-email-edit">
            <JeaInput
              type="email"
              placeholder="new@example.com"
              value={emailNew}
              onChange={(e) => setEmailNew(e.target.value)}
              data-testid="contact-email-new-input"
            />
            <div className="flex gap-2">
              <JeaButton
                size="sm"
                onClick={startEmail}
                loading={busy === "start-email"}
                data-testid="contact-email-send-btn"
              >
                Send verification
              </JeaButton>
              <JeaButton
                size="sm"
                variant="outline-neutral"
                onClick={() => setEmailStage("idle")}
                data-testid="contact-email-cancel-btn"
              >
                Cancel
              </JeaButton>
            </div>
          </div>
        )}
        {emailStage === "verifying" && (
          <div className="space-y-3 pl-7" data-testid="contact-email-verify">
            <div className="text-[12px] text-text-secondary">
              Enter the 6-digit code sent to {emailNew}.
            </div>
            {digitInput(emailCode, setEmailCode, "contact-email-digit")}
            <div className="flex gap-2">
              <JeaButton
                size="sm"
                onClick={confirmEmail}
                loading={busy === "confirm-email"}
                disabled={emailCode.length !== 6}
                data-testid="contact-email-verify-btn"
              >
                Confirm
              </JeaButton>
              <JeaButton
                size="sm"
                variant="outline-neutral"
                onClick={() => { setEmailStage("idle"); setEmailCode(""); setEmailNew(""); }}
                data-testid="contact-email-verify-cancel-btn"
              >
                Cancel
              </JeaButton>
            </div>
          </div>
        )}
      </div>

      {/* Phone row */}
      <div
        className="rounded-xl border border-border-default bg-bg-primary p-4 space-y-3"
        data-testid="contact-phone-row"
      >
        <div className="flex items-start gap-3">
          <PhoneIcon size={20} className="text-success mt-0.5" />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-white text-[14px] font-semibold">Phone</span>
              {data.phone_verified ? (
                <JeaBadge variant="success" data-testid="contact-phone-verified-badge">
                  <CheckCircle2 size={12} /> Verified
                </JeaBadge>
              ) : data.phone_number ? (
                <JeaBadge variant="warning" data-testid="contact-phone-unverified-badge">
                  <AlertCircle size={12} /> Unverified
                </JeaBadge>
              ) : (
                <JeaBadge variant="neutral" data-testid="contact-phone-none-badge">Not set</JeaBadge>
              )}
            </div>
            <div className="text-text-secondary text-[13px] mt-0.5 jea-mono" data-testid="contact-phone-value">
              {data.phone_number || "—"}
            </div>
          </div>
          {phoneStage === "idle" && (
            <div className="flex gap-2">
              <JeaButton
                size="sm"
                variant="outline-neutral"
                onClick={() => { setPhoneStage("editing"); setPhoneNew(""); }}
                data-testid="contact-phone-change-btn"
              >
                <Edit3 size={14} /> {data.phone_number ? "Change" : "Add"}
              </JeaButton>
              {data.phone_number && (
                <JeaButton
                  size="sm"
                  variant="outline-neutral"
                  onClick={removePhone}
                  loading={busy === "remove-phone"}
                  data-testid="contact-phone-remove-btn"
                >
                  <Trash2 size={14} />
                </JeaButton>
              )}
            </div>
          )}
        </div>
        {phoneStage === "editing" && (
          <div className="space-y-2 pl-7" data-testid="contact-phone-edit">
            <PhoneInput
              international
              defaultCountry="US"
              value={phoneNew}
              onChange={(v) => setPhoneNew(v || "")}
              placeholder="+1 555 555 0123"
              className="jea-phone-input"
              data-testid="contact-phone-new-input"
            />
            <div className="flex gap-2">
              <JeaButton
                size="sm"
                onClick={startPhone}
                loading={busy === "start-phone"}
                data-testid="contact-phone-send-btn"
              >
                Send verification
              </JeaButton>
              <JeaButton
                size="sm"
                variant="outline-neutral"
                onClick={() => setPhoneStage("idle")}
                data-testid="contact-phone-cancel-btn"
              >
                Cancel
              </JeaButton>
            </div>
          </div>
        )}
        {phoneStage === "verifying" && (
          <div className="space-y-3 pl-7" data-testid="contact-phone-verify">
            <div className="text-[12px] text-text-secondary">
              Enter the 6-digit code sent to {phoneNew}.
            </div>
            {digitInput(phoneCode, setPhoneCode, "contact-phone-digit")}
            <div className="flex gap-2">
              <JeaButton
                size="sm"
                onClick={confirmPhone}
                loading={busy === "confirm-phone"}
                disabled={phoneCode.length !== 6}
                data-testid="contact-phone-verify-btn"
              >
                Confirm
              </JeaButton>
              <JeaButton
                size="sm"
                variant="outline-neutral"
                onClick={() => { setPhoneStage("idle"); setPhoneCode(""); setPhoneNew(""); }}
                data-testid="contact-phone-verify-cancel-btn"
              >
                Cancel
              </JeaButton>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

/* ========================= TwoFactorMethodsPanel ========================= */
const TwoFactorMethodsPanel = () => {
  const [status, setStatus] = useState(null);
  const [contact, setContact] = useState(null);
  const [busy, setBusy] = useState(null);
  const [newCodes, setNewCodes] = useState(null);

  const load = async () => {
    try {
      const [{ data: s }, c] = await Promise.all([
        apiClient.get("/account/2fa/status"),
        contactApi.get(),
      ]);
      setStatus(s);
      setContact(c);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  if (!status || !contact) {
    return <div className="text-text-muted text-[13px]">Loading 2FA methods…</div>;
  }

  const setPrimary = async (channel) => {
    setBusy(`primary-${channel}`);
    try {
      await apiClient.post("/account/2fa/primary", { channel });
      toast.success(`${CHANNEL_LABEL[channel]} is now your primary 2FA method.`);
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(null); }
  };
  const unenroll = async (channel) => {
    setBusy(`remove-${channel}`);
    try {
      await apiClient.delete(`/account/2fa/${channel}`);
      toast.success(`${CHANNEL_LABEL[channel]} removed.`);
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(null); }
  };
  const regen = async () => {
    setBusy("regen-backup");
    try {
      const { data } = await apiClient.post("/account/2fa/backup-codes/regenerate");
      setNewCodes(data.codes);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(null); }
  };
  const toggleVoice = async (enabled) => {
    setBusy("voice-toggle");
    try {
      await contactApi.setVoiceFallback(enabled);
      await load();
      toast.success(`Voice fallback ${enabled ? "enabled" : "disabled"}.`);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(null); }
  };

  const enrolled = status.enrolled_channels || [];
  const canOfferVoiceToggle = enrolled.includes("sms");

  return (
    <div className="space-y-4" data-testid="twofactor-methods-panel">
      {newCodes && (
        <div
          className="rounded-xl border border-success/40 bg-success/5 p-4 space-y-3"
          data-testid="new-backup-codes-display"
        >
          <div className="text-[14px] font-semibold text-white">
            New backup codes — save these now. Each works once.
          </div>
          <pre
            className="rounded-md border border-border-default bg-bg-tertiary px-4 py-3 text-[13px] font-mono text-white leading-relaxed"
            data-testid="new-backup-codes-list"
          >
            {newCodes.join("\n")}
          </pre>
          <div className="flex gap-2">
            <JeaButton
              size="sm"
              variant="outline-neutral"
              onClick={() => {
                navigator.clipboard.writeText(newCodes.join("\n"));
                toast.success("Copied to clipboard.");
              }}
              data-testid="new-backup-copy"
            >
              <Copy size={14} /> Copy
            </JeaButton>
            <JeaButton
              size="sm"
              variant="outline-neutral"
              onClick={() => {
                const blob = new Blob([newCodes.join("\n")], { type: "text/plain" });
                const a = document.createElement("a");
                a.href = URL.createObjectURL(blob);
                a.download = "jea-backup-codes.txt";
                a.click();
                URL.revokeObjectURL(a.href);
              }}
              data-testid="new-backup-download"
            >
              <Download size={14} /> Download
            </JeaButton>
            <JeaButton
              size="sm"
              onClick={() => setNewCodes(null)}
              data-testid="new-backup-dismiss"
            >
              Done
            </JeaButton>
          </div>
        </div>
      )}

      {enrolled.length === 0 && (
        <div className="rounded-xl border border-warning/40 bg-warning/5 p-4 text-[13px] text-warning">
          You don't have any 2FA methods enrolled yet.{" "}
          <a href="/setup-2fa" className="underline hover:text-success">
            Set one up now
          </a>.
        </div>
      )}

      {enrolled.map((channel) => {
        const Icon = CHANNEL_ICON[channel] || ShieldCheck;
        const isPrimary = status.primary_channel === channel;
        return (
          <div
            key={channel}
            className="rounded-xl border border-border-default bg-bg-primary p-4 flex items-start gap-3"
            data-testid={`twofactor-row-${channel}`}
          >
            <Icon size={20} className="text-success mt-0.5" />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-white text-[14px] font-semibold">
                  {CHANNEL_LABEL[channel]}
                </span>
                {isPrimary && (
                  <JeaBadge variant="success" data-testid={`twofactor-primary-badge-${channel}`}>
                    <Star size={12} /> Primary
                  </JeaBadge>
                )}
              </div>
              <div className="text-text-secondary text-[12px] mt-0.5">
                {channel === "sms" || channel === "voice"
                  ? contact.phone_number || "No phone on file"
                  : channel === "email"
                  ? contact.email || "No email on file"
                  : "Code generated by your authenticator app"}
              </div>
            </div>
            <div className="flex gap-2">
              {!isPrimary && (
                <JeaButton
                  size="sm"
                  variant="outline-neutral"
                  onClick={() => setPrimary(channel)}
                  loading={busy === `primary-${channel}`}
                  data-testid={`twofactor-primary-${channel}`}
                >
                  Set primary
                </JeaButton>
              )}
              <JeaButton
                size="sm"
                variant="outline-neutral"
                onClick={() => unenroll(channel)}
                loading={busy === `remove-${channel}`}
                data-testid={`twofactor-remove-${channel}`}
              >
                <Trash2 size={14} />
              </JeaButton>
            </div>
          </div>
        );
      })}

      <div className="flex flex-wrap gap-2 pt-2">
        <a href="/setup-2fa" data-testid="twofactor-add-link">
          <JeaButton size="sm">
            <Plus size={14} /> Add another method
          </JeaButton>
        </a>
        <JeaButton
          size="sm"
          variant="outline-neutral"
          onClick={regen}
          loading={busy === "regen-backup"}
          data-testid="twofactor-regen-backup"
        >
          <RefreshCw size={14} /> Regenerate backup codes
        </JeaButton>
      </div>

      {canOfferVoiceToggle && (
        <div
          className="rounded-xl border border-border-default bg-bg-primary p-4 flex items-center gap-3"
          data-testid="voice-fallback-row"
        >
          <Volume2 size={20} className="text-text-secondary" />
          <div className="flex-1">
            <div className="text-white text-[14px] font-semibold">Voice fallback on SMS failure</div>
            <div className="text-text-secondary text-[12px] mt-0.5">
              When ON, we'll offer a voice call if an SMS doesn't arrive.
            </div>
          </div>
          <JeaToggle
            checked={!!contact.voice_fallback_enabled}
            onChange={(v) => toggleVoice(v)}
            data-testid="voice-fallback-toggle"
          />
        </div>
      )}
    </div>
  );
};

export { ContactMethodsPanel, TwoFactorMethodsPanel, Section };
